import type { ReactElement } from 'react';
import type { PrimaryGoal } from '@shared/types';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableText } from '../../components/Editable';
import { ImageSlot } from '../../components/ImageSlot';
import { useContentLibrary } from '../../state/ContentLibraryContext';
import { Frame, HeadRow, SubCard, focalPointStyle } from './primitives';
import { ReportingDashboardArt } from './ReportingDashboardArt';

const HEADLINE_DEFAULT = "Know what's working. Do more of it.";
const BODY_DEFAULT =
  "We track performance against the goal every month and shift budget toward what's converting, so the plan keeps getting sharper.";

type ReportingAction = { title: string; support: string };

const DEFAULT_ACTIONS: ReportingAction[] = [
  { title: 'Track calls & forms', support: 'See every lead that comes in, and where it came from.' },
  { title: 'Prune weak keywords & creative', support: "Cut what isn't converting so budget goes further." },
  { title: 'Shift spend toward winners', support: 'Move budget toward the channels driving results.' },
  { title: 'Review strategy regularly', support: 'Revisit the plan on a set cadence, not just at renewal.' },
];

/**
 * K3d: reporting's headline/body/bullets used to be one fixed,
 * conversion-flavored set ("Track calls & forms") regardless of the
 * campaign's actual goal - real proposals never hit the component-level
 * fallback above because `src/state/document.ts` seeds `content.headline`/
 * `body`/`bullets` from `config/content-library.json`'s `reporting_v3` at
 * assembly time, so an awareness campaign (e.g. b6f35be2, primaryGoal
 * `brand_awareness`) shipped the exact same lead-gen language as a sales
 * campaign. Keyed by `objective.primaryGoal`; `null`/unrecognized keeps the
 * original copy.
 */
const GOAL_HEADLINES: Partial<Record<PrimaryGoal, string>> = {
  brand_awareness: "Know what's landing. Do more of it.",
  product_launch: "Know what's landing. Do more of it.",
  sales_conversion: "Know what's selling. Do more of it.",
  customer_retention: "Know who's coming back. Do more of it.",
  market_expansion: "Know where it's working. Do more of it.",
};

const GOAL_BODIES: Partial<Record<PrimaryGoal, string>> = {
  brand_awareness:
    "We track reach, frequency, and brand-response signals every month and shift budget toward what's building awareness.",
  product_launch:
    "We track launch performance against the goal every month and shift budget toward what's landing, so the plan keeps getting sharper.",
  sales_conversion:
    "We track sales and revenue against the goal every month and shift budget toward what's converting, so the plan keeps getting sharper.",
  customer_retention:
    "We track repeat customers and engagement every month and shift budget toward what's bringing them back.",
  market_expansion:
    "We track performance by market against the goal every month and shift budget toward what's working in each one.",
};

const GOAL_ACTIONS: Partial<Record<PrimaryGoal, ReportingAction[]>> = {
  brand_awareness: [
    { title: 'Track reach & frequency', support: 'See how many people saw the campaign, and how often.' },
    { title: 'Watch brand-response signals', support: 'Direct traffic and branded search, not just clicks.' },
    { title: 'Shift spend toward top channels', support: 'Move budget toward the placements building awareness.' },
    { title: 'Review strategy regularly', support: 'Revisit the plan on a set cadence, not just at renewal.' },
  ],
  product_launch: [
    { title: 'Track launch-week signals', support: 'Watch reach, engagement, and early conversions together.' },
    { title: 'Prune weak creative fast', support: "Cut what isn't landing while there's still budget to shift." },
    { title: 'Shift spend toward winners', support: 'Move budget toward the channels driving results.' },
    { title: 'Review strategy regularly', support: 'Revisit the plan on a set cadence, not just at renewal.' },
  ],
  sales_conversion: [
    { title: 'Track purchases & revenue', support: 'See every sale, and which channel gets the credit.' },
    { title: 'Prune weak keywords & creative', support: "Cut what isn't converting so budget goes further." },
    { title: 'Shift spend toward winners', support: 'Move budget toward the channels driving results.' },
    { title: 'Review strategy regularly', support: 'Revisit the plan on a set cadence, not just at renewal.' },
  ],
  customer_retention: [
    { title: 'Track repeat customers', support: 'See who is returning, and which channel brought them back.' },
    { title: 'Watch engagement signals', support: 'Opens, visits, and repeat purchases, not just first clicks.' },
    { title: 'Shift spend toward winners', support: 'Move budget toward the channels driving results.' },
    { title: 'Review strategy regularly', support: 'Revisit the plan on a set cadence, not just at renewal.' },
  ],
  market_expansion: [
    { title: 'Track performance by market', support: 'See which geographies and audiences are responding.' },
    { title: 'Prune weak keywords & creative', support: "Cut what isn't converting so budget goes further." },
    { title: 'Shift spend toward winners', support: 'Move budget toward the channels driving results.' },
    { title: 'Review strategy regularly', support: 'Revisit the plan on a set cadence, not just at renewal.' },
  ],
};

function reportingHeadlineDefault(goal: PrimaryGoal | null | undefined): string {
  return (goal && GOAL_HEADLINES[goal]) || HEADLINE_DEFAULT;
}

function reportingBodyDefault(goal: PrimaryGoal | null | undefined): string {
  return (goal && GOAL_BODIES[goal]) || BODY_DEFAULT;
}

function reportingActionDefaults(goal: PrimaryGoal | null | undefined): ReportingAction[] {
  return (goal && GOAL_ACTIONS[goal]) || DEFAULT_ACTIONS;
}

/**
 * One `bullets` entry encodes a whole action card as `"Title — Support"`
 * (config/content-library.json `reporting_v3.bullets` - one string per card,
 * an em dash between the two halves - matching copyBudgets.ts's single
 * 96-char "bullets (4): 26 title / 70 support" row, which budgets the pair
 * as one field rather than two). No em dash means the whole string is the
 * title with no support line.
 */
function splitBullet(raw: string | undefined): { title: string; support: string } {
  if (!raw) return { title: '', support: '' };
  const sep = raw.indexOf(' — ');
  if (sep === -1) return { title: raw, support: '' };
  return { title: raw.slice(0, sep), support: raw.slice(sep + 3) };
}
function joinBullet(title: string, support: string): string {
  return support ? `${title} — ${support}` : title;
}

/**
 * reporting (docs/momentum-v3-slides.md `reporting`; slide audit Slide 20):
 * light, a fixed 2x2 grid of action cards sharing top/bottom edges with the
 * image panel. Headline/body/bullets are seeded at assembly from the
 * content library's `reporting_v3` overlay (`src/state/document.ts`
 * `staticSection`, `config/content-library.json`) - a section that has
 * never been edited beyond that seed still carries it verbatim in
 * `content.headline`/`body`/`bullets`, which is why the goal-aware swap
 * below (K3d) compares against the SAME library values read here via
 * `useContentLibrary()`, rather than only covering the empty-section case:
 * on a real proposal (e.g. b6f35be2, primaryGoal `brand_awareness`) the
 * section is never empty, it just hasn't been customized past the generic
 * lead-gen-flavored seed - so a naive `content.headline || fallback` check
 * would never fire. Once the rep or agent edits ANY of headline/body/
 * bullets away from the seed, that field (or the whole bullets set) is
 * treated as authored and rendered as-is, goal or no goal.
 */
export function ReportingPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const { library } = useContentLibrary();
  const seed = library.staticSections['reporting_v3'];
  const goal = proposal.objective.primaryGoal;
  const headlineDefault = reportingHeadlineDefault(goal);
  const bodyDefault = reportingBodyDefault(goal);
  const actionDefaults = reportingActionDefaults(goal);

  const rawHeadline = section.content.headline;
  const isSeedHeadline = !rawHeadline || rawHeadline === seed?.title;
  const headline = isSeedHeadline ? headlineDefault : rawHeadline;

  const rawBody = section.content.body;
  const isSeedBody = !rawBody || rawBody === seed?.body;
  const body = isSeedBody ? bodyDefault : rawBody;

  const rawBullets = section.content.bullets ?? [];
  const seedBullets = seed?.bullets ?? [];
  const isSeedBullets =
    rawBullets.length === 0 ||
    (rawBullets.length === seedBullets.length && rawBullets.every((b, i) => b === seedBullets[i]));
  const authoredBullets = isSeedBullets ? [] : rawBullets;
  const imageUrl = section.content.imageUrl;

  const cards = actionDefaults.map((fallbackCard, index) => {
    const raw = authoredBullets[index];
    if (!raw) return fallbackCard;
    const { title, support } = splitBullet(raw);
    return { title: title || fallbackCard.title, support };
  });

  const saveCard = (index: number, next: { title?: string; support?: string }) => {
    const nextBullets = actionDefaults.map((fallbackCard, i) => {
      const raw = authoredBullets[i];
      return raw ? raw : joinBullet(fallbackCard.title, fallbackCard.support);
    });
    const current = splitBullet(nextBullets[index]);
    nextBullets[index] = joinBullet(next.title ?? current.title, next.support ?? current.support);
    actions.saveContent(section, { bullets: nextBullets });
  };

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-reporting-root"
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-full-pane">
          <span className="mv3-eyebrow">Reporting & optimization</span>
          <HeadRow
            title={
              <EditableText
                value={headline}
                field="headline"
                placeholder={headlineDefault}
                readOnly={readOnly}
                onSave={(next) => actions.saveContent(section, { headline: next })}
              />
            }
          />
          <div className="mv3-reporting-body">
            <EditableText
              value={body}
              field="body"
              placeholder="Tie the reporting cadence back to the campaign goal"
              readOnly={readOnly}
              variant="lede"
              onSave={(next) => actions.saveContent(section, { body: next })}
            />
          </div>
          <div className="mv3-reporting-cols">
            <div className="mv3-reporting-cards">
              {cards.map((card, index) => (
                <SubCard tone="light" className="mv3-reporting-card" key={index}>
                  <span className="mv3-reporting-num">{String(index + 1).padStart(2, '0')}</span>
                  <EditableText
                    value={card.title}
                    field="bullets"
                    placeholder="Action"
                    readOnly={readOnly}
                    variant="headline"
                    onSave={(next) => saveCard(index, { title: next })}
                  />
                  <EditableText
                    value={card.support}
                    field="bullets"
                    placeholder="One short support line"
                    readOnly={readOnly}
                    onSave={(next) => saveCard(index, { support: next })}
                  />
                </SubCard>
              ))}
            </div>
            <div className="mv3-reporting-panel">
              {imageUrl ? (
                <img src={imageUrl} alt="Reporting" style={focalPointStyle(section)} />
              ) : (
                <ReportingDashboardArt className="mv3-reporting-art" />
              )}
              {!readOnly ? (
                <ImageSlot
                  url={imageUrl}
                  alt="Reporting"
                  readOnly={readOnly}
                  onSave={(next) => actions.saveContent(section, { imageUrl: next })}
                  className="mv3-reporting-image-affordance"
                  hideImage
                  proposalId={proposal.proposalId}
                  defaultQuery={section.title}
                />
              ) : null}
            </div>
          </div>
        </div>
      </Frame>
    </PageShell>
  );
}
